import type {
  ComponentVisibility,
  ValidationRule,
  ValidationRules,
} from '@commenda/types';
import { isValueEmpty } from './form-state';

/** Returns whether a validation rule is active (enabled and its when condition met, if any). */
export function isRuleActive(
  rule: ValidationRule | boolean | undefined,
  isFieldVisible: (v?: ComponentVisibility) => boolean
): boolean {
  if (!rule) return false;
  if (typeof rule === 'boolean') return rule;
  if (rule.enabled === false) return false;
  if (rule.when && !isFieldVisible({ condition: rule.when })) return false;
  return true;
}

/**
 * The entries a field holds, whatever shape it stores them in. Only selected people count: an
 * entry left behind by a deselection is not one.
 */
function entriesOf(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) {
    return value.filter(
      (entry): entry is Record<string, unknown> =>
        !!entry && typeof entry === 'object' && !Array.isArray(entry)
    );
  }
  if (!value || typeof value !== 'object') return [];
  const { selectedIds, entries } = value as {
    selectedIds?: unknown;
    entries?: Record<string, unknown>;
  };
  if (!Array.isArray(selectedIds)) return [];
  return selectedIds.reduce<Record<string, unknown>[]>((acc, id) => {
    const entry = typeof id === 'string' ? entries?.[id] : undefined;
    if (entry && typeof entry === 'object' && !Array.isArray(entry))
      acc.push(entry as Record<string, unknown>);
    return acc;
  }, []);
}

/** What one answer says was chosen. A CheckboxGroup's answer is its checked keys, not the record. */
function answeredValues(answer: unknown): Set<string> {
  if (answer === null || answer === undefined || answer === '') return new Set();
  if (Array.isArray(answer))
    return new Set(answer.filter((v): v is string => typeof v === 'string'));
  if (typeof answer === 'object')
    return new Set(
      Object.entries(answer as Record<string, unknown>)
        .filter(([, checked]) => checked === true)
        .map(([key]) => key)
    );
  return new Set([String(answer)]);
}

/** The values `rule` asks for that no entry answers, in the order the rule lists them. */
export function uncoveredEntryValues(
  rule: NonNullable<ValidationRules['entryCoverage']>,
  value: unknown
): string[] {
  const covered = entriesOf(value).reduce<Set<string>>((acc, entry) => {
    answeredValues(entry[rule.field]).forEach((v) => acc.add(v));
    return acc;
  }, new Set());
  return rule.values.filter((wanted) => !covered.has(wanted));
}

/** The message for an `entryCoverage` failure, or null when the rule is satisfied. */
export function entryCoverageError(
  rule: ValidationRules['entryCoverage'] | undefined,
  value: unknown,
  isFieldVisible: (v?: ComponentVisibility) => boolean
): string | null {
  if (!rule || !isRuleActive(rule, isFieldVisible)) return null;
  const missing = uncoveredEntryValues(rule, value);
  if (missing.length === 0) return null;
  if (rule.message) return rule.message;
  const names = missing.map((v) => rule.labels?.[v] ?? v);
  return `Still needed: ${names.join(', ')}.`;
}

/** How long a value counts as, for the length rules. Strings and arrays both measure. */
function lengthOf(value: unknown): number {
  if (typeof value === 'string' || Array.isArray(value)) return value.length;
  return 0;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HOSTNAME_TLD = /\.[a-z]{2,}$/i;

/**
 * An https:// URL or a bare domain; the forms accept both. Parsed rather than pattern-matched,
 * because a pattern for this backtracks exponentially on a URL carrying a query string.
 */
function isHttpsUrlOrDomain(value: string): boolean {
  try {
    const url = new URL(value.includes('://') ? value : `https://${value}`);
    return url.protocol === 'https:' && HOSTNAME_TLD.test(url.hostname);
  } catch {
    return false;
  }
}

/**
 * The message a value fails its rules with, or null when it passes.
 *
 * Pure, so the three renderers that each kept their own copy of this can share one. They had
 * drifted apart — the exported hook never checked email, url or notSameAs, and a linked sub-form
 * checked only required and pattern — so a rule the form declared went unenforced depending on
 * where the field happened to be drawn.
 *
 * Rules a single caller owns stay with that caller: uniqueInRepeatable needs the sibling entries,
 * and a sub-form's same-value binding needs the parent form's answers.
 */
export function fieldValidationError(
  value: unknown,
  rules: ValidationRules | undefined,
  options: {
    isFieldVisible: (v?: ComponentVisibility) => boolean;
    /** Another field's value, for notSameAs. Omitted where the caller holds no form data. */
    resolveField?: (field: string) => unknown;
  }
): string | null {
  if (!rules) return null;
  const { isFieldVisible, resolveField } = options;
  const active = (rule: ValidationRule | boolean | undefined): boolean => isRuleActive(rule, isFieldVisible);

  if (active(rules.required) && isValueEmpty(value)) {
    return (typeof rules.required === 'object' ? rules.required.message : null) ?? 'This field is required';
  }

  const coverage = entryCoverageError(rules.entryCoverage, value, isFieldVisible);
  if (coverage) return coverage;

  if (rules.minLength && active(rules.minLength)) {
    const min = rules.minLength.value as number;
    if (lengthOf(value) < min) return rules.minLength.message || `Minimum ${min} items required`;
  }

  if (rules.maxLength && active(rules.maxLength)) {
    const max = rules.maxLength.value as number;
    if (lengthOf(value) > max) return rules.maxLength.message || `Maximum ${max} items allowed`;
  }

  if (active(rules.email) && value && !EMAIL.test(String(value))) {
    return (typeof rules.email === 'object' ? rules.email.message : null) ?? 'Invalid email address';
  }

  if (active(rules.url) && value && !isHttpsUrlOrDomain(String(value))) {
    return (typeof rules.url === 'object' ? rules.url.message : null) ?? 'Please enter a valid HTTPS URL or domain';
  }

  if (rules.pattern && active(rules.pattern) && value) {
    const pattern = typeof rules.pattern === 'object' ? rules.pattern.value : null;
    if (typeof pattern === 'string' && pattern) {
      let matches = true;
      try {
        matches = new RegExp(pattern).test(String(value));
      } catch {
        // An unusable pattern is the form's problem, not the answer's.
        console.warn(`Invalid regex pattern: ${pattern}`);
      }
      if (!matches) return rules.pattern.message ?? 'Invalid format';
    }
  }

  if (active(rules.notSameAs) && rules.notSameAs?.field && resolveField) {
    const other = resolveField(rules.notSameAs.field);
    if (value && other && value === other) {
      return rules.notSameAs.message || 'This value cannot be the same as another field';
    }
  }

  return null;
}
